import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import ExtensionIcon from '@mui/icons-material/Extension';
import LoginIcon from '@mui/icons-material/Login';
import { Alert, Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';

import { EXTENSION_STORE_URL, findSignInExtension, signInUrlOf, signInWithExtension } from '@/utils';

import type { CredentialsData } from '@/types';

/**
 * Credentials that sign the recipient in without showing the password (Yuri, 29/09/2026). The page
 * never displays them: it hands them to the SharePass extension, which asks the person in its own
 * window and fills the site's sign-in form.
 */

const Frame: FC<{ icon: ReactNode; title: string; text: string; children?: ReactNode }> = ({
    icon,
    title,
    text,
    children,
}) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                p: 3,
                maxWidth: 400,
                mx: 'auto',
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main + '20',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.primary.main,
                    '& svg': { fontSize: 40 },
                }}
            >
                {icon}
            </Box>
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {text}
                </Typography>
            </Box>
            {children}
        </Box>
    );
};

type Detection = 'checking' | 'missing' | string;

/** Looks for the extension; `retry` looks again (after installing it). */
const useSignInExtension = () => {
    const [found, setFound] = useState<Detection>('checking');
    const look = useCallback(() => findSignInExtension().then((id) => setFound(id ?? 'missing')), []);
    useEffect(() => {
        void look();
    }, [look]);
    const retry = useCallback(() => {
        setFound('checking');
        void look();
    }, [look]);
    return { found, retry };
};

const InstallExtension: FC<{ onRetry: () => void; opened: boolean }> = ({ onRetry, opened }) => (
    <Frame
        icon={<ExtensionIcon />}
        title="You need the SharePass extension"
        text={
            opened
                ? 'This link signs you in to a site without showing you the password, and only the SharePass extension for Chrome can do that. Install it, then press the button below.'
                : 'This link signs you in to a site without showing you the password. It needs the SharePass extension for Chrome: install it, then come back to this page. The link is not opened until you do.'
        }
    >
        <Button fullWidth variant="contained" size="large" href={EXTENSION_STORE_URL} target="_blank" rel="noopener">
            Get the extension
        </Button>
        <Button fullWidth variant="text" onClick={onRetry}>
            I have installed it
        </Button>
    </Frame>
);

/**
 * Before the link is opened: a sign-in link waits for the extension, since opening it spends it (a
 * one-time link) and the password is never shown instead.
 */
export const SignInGate: FC<{ children: ReactNode }> = ({ children }) => {
    const { found, retry } = useSignInExtension();
    if (found === 'checking') {
        return (
            <Frame icon={<ExtensionIcon />} title="Looking for the SharePass extension" text="One moment…">
                <CircularProgress size={28} />
            </Frame>
        );
    }
    if (found === 'missing') return <InstallExtension onRetry={retry} opened={false} />;
    return <>{children}</>;
};

/** Once open: the site and the account, and the button that hands them to the extension. */
export const SignInPanel: FC<{ credentials: CredentialsData; submit: boolean }> = ({ credentials, submit }) => {
    const { found, retry } = useSignInExtension();
    const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
    const [error, setError] = useState<string | null>(null);
    const url = signInUrlOf(credentials.hostname);
    const host = url ? new URL(url).host : null;

    if (!url || !host) {
        return (
            <Frame
                icon={<LoginIcon />}
                title="This sign-in cannot be used"
                text="The sender did not save a valid address for the site. Ask them to send it again."
            />
        );
    }
    if (found === 'checking') {
        return (
            <Frame icon={<LoginIcon />} title={`Sign in to ${host}`} text="Looking for the SharePass extension…">
                <CircularProgress size={28} />
            </Frame>
        );
    }
    if (found === 'missing') return <InstallExtension onRetry={retry} opened />;

    const handOff = async () => {
        setError(null);
        setState('sending');
        const problem = await signInWithExtension(found, {
            url,
            username: credentials.user ?? '',
            password: credentials.password ?? '',
            ...(credentials.secret ? { totpSeed: credentials.secret } : {}),
            submit,
        });
        if (problem) {
            setError(problem);
            setState('idle');
        } else {
            setState('sent');
        }
    };

    return (
        <Frame
            icon={<LoginIcon />}
            title={`Sign in to ${host}`}
            text={
                state === 'sent'
                    ? 'Confirm in the SharePass window. It opens the site and signs you in.'
                    : `${credentials.user ? `As ${credentials.user}. ` : ''}The sender shared access, not the password: SharePass signs you in and you never see it.`
            }
        >
            {error && (
                <Alert severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            )}
            <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<LoginIcon />}
                onClick={handOff}
                disabled={state === 'sending'}
                sx={{ py: 1.5, fontWeight: 600 }}
            >
                {state === 'sent' ? 'Sign in again' : state === 'sending' ? 'Opening…' : 'Sign in'}
            </Button>
        </Frame>
    );
};
